import React from 'react';
import { Link } from 'react-router-dom';
import { Check as CheckIcon } from '@mui/icons-material';
import UIBox from 'components/ui/UIBox';
import UIText from 'components/ui/UIText';
import ProgressBar from 'components/ProgressBar';
import { hexToRgba } from 'components/cefrMeta';
import { dieslova } from './patternSuperGroups';

interface IPatternRowProps {
  index: number;
  title: string;
  total: number;
  learned: number;
  /**
   * Number of verbs already in the user's vocabulary (learned OR active
   * speech). Drives the "added" segment of the progress bar — when 0 the
   * bar is hidden entirely so rows the user hasn't touched stay quiet.
   */
  added: number;
  color: string;
  link: string;
  /** Override the default "{total} дієслів" caption (e.g. "X похідних"). */
  subtitle?: string;
}

// Mirrors the visual style of SubCategoryRow used on /wordset/level-based/A1
// so the patterns list reads as a peer of word-set sub-categories. Kept
// separate from SubCategoryRow because the irregular-verb domain only tracks
// learned + total (no addedWords concept).
const PatternRow: React.FC<IPatternRowProps> = ({
  index,
  title,
  total,
  learned,
  added,
  color,
  link,
  subtitle,
}) => {
  const done = total > 0 && learned >= total;

  return (
    <Link style={{ display: 'block', width: '100%' }} to={link}>
      <UIBox
        sx={{
          alignItems: 'center',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          cursor: 'pointer',
          display: 'flex',
          gap: { xs: 1.5, md: 2.25 },
          p: { xs: '10px 12px', md: '12px 18px' },
          transition: 'border-color .16s ease, background .16s ease',
          '&:hover': {
            bgcolor: hexToRgba(color, 0.04),
            borderColor: color,
          },
          '&:hover .sc-arrow': {
            transform: 'translateX(4px)',
          },
        }}
      >
        {/* Numbered badge / done checkmark — same chrome as SubCategoryRow */}
        <UIBox
          sx={{
            alignItems: 'center',
            bgcolor: done ? 'success.main' : hexToRgba(color, 0.12),
            border: done ? 'none' : '1px solid',
            borderColor: done ? 'transparent' : hexToRgba(color, 0.25),
            borderRadius: '50%',
            color: done ? '#fff' : color,
            display: 'inline-flex',
            flex: 'none',
            fontSize: 15,
            fontWeight: 800,
            height: 38,
            justifyContent: 'center',
            width: 38,
          }}
        >
          {done ? <CheckIcon sx={{ fontSize: 19 }} /> : index + 1}
        </UIBox>

        {/* Title + verb count — sized for the longest pattern label
            ("Укорочення голосної (-eed/-ead → -ed)" ≈ 36 chars). All known
            labels fit on a single line at this width; if a future label
            grows past it the text wraps cleanly instead of clipping. */}
        <UIBox sx={{ flex: 'none', minWidth: 0, width: { xs: 'auto', md: 360 } }}>
          <UIText
            sx={{
              fontSize: { xs: 14, md: 16 },
              fontWeight: 700,
              lineHeight: 1.25,
            }}
          >
            {title}
          </UIText>
          <UIText color={'primary.subtitle'} sx={{ fontSize: 13, mt: 0.25 }}>
            {subtitle ?? `${total} ${dieslova(total)}`}
          </UIText>
        </UIBox>

        {/* Inline progress — same ProgressBar used by SubCategoryRow.
            `added` here is the real count of verbs in the user's vocabulary;
            ProgressBar returns null when it's 0, which is the right default
            for rows the user hasn't started on. */}
        <UIBox sx={{ display: { xs: 'none', md: 'block' }, flex: 1, minWidth: 0 }}>
          <ProgressBar
            addedValue={added}
            learnedValue={learned}
            totalValue={total}
            minWidth={'100%'}
            maxWidth={'100%'}
            width={'100%'}
          />
        </UIBox>

        <UIBox
          className={'sc-arrow'}
          component={'span'}
          sx={{
            color,
            display: 'inline-block',
            flex: 'none',
            fontSize: 20,
            fontWeight: 700,
            transition: 'transform .25s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          {'→'}
        </UIBox>
      </UIBox>
    </Link>
  );
};

export default PatternRow;
